import { css } from 'aphrodite';
import * as React from 'react';
import type {
	CoachingAdherenceRepScore,
	CoachingAdherenceScoreboard,
} from '../../../queries/AIConversation/useCoachingAdherenceScoreboardQuery';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import {
	MIN_CALLS_FOR_SHARE,
	followedShare,
	formatPercent,
	isNeverFollower,
	shareTier,
	tierPillColor,
} from './scoreboardMetrics';
import { styleSheet } from './scoreboard.styles';
import { ScoreboardRepCalls } from './ScoreboardRepCalls';

const COLUMN_COUNT = 9;

interface IProps {
	scoreboard: CoachingAdherenceScoreboard;
	reps: CoachingAdherenceRepScore[];
	initialExpandedRepId?: string | null;
	/** The range as requested, not the server's echo, so the drill-down matches the API call exactly. */
	range: { start: string; end: string };
	onSelectCall: (aiConversationId: string) => void;
}

export function ScoreboardTable({ scoreboard, reps, initialExpandedRepId = null, range, onSelectCall }: IProps) {
	const [expandedRepId, setExpandedRepId] = React.useState<string | null>(initialExpandedRepId);
	const { followedScore } = scoreboard;

	return (
		<div className={css(styleSheet.tableWrap)}>
			<table className={css(styleSheet.table)}>
				<thead>
					<tr>
						<th className={css(styleSheet.th, styleSheet.alignLeft)}>Rep</th>
						<th className={css(styleSheet.th)}>Calls past hook</th>
						<th className={css(styleSheet.th)}>Followed ({followedScore}+)</th>
						<th className={css(styleSheet.th)}>Share followed</th>
						<th className={css(styleSheet.th)}>Hook-rejected</th>
						<th className={css(styleSheet.th)}>Of those {followedScore}+</th>
						<th className={css(styleSheet.th)}>Booked</th>
						<th className={css(styleSheet.th)}>Booked on followed calls</th>
						<th className={css(styleSheet.th)}>Not scored</th>
					</tr>
				</thead>
				<tbody>
					{reps.map(rep => {
						const expanded = expandedRepId === rep.userId;
						const share = followedShare(rep);
						const thin = rep.pastHookCalls < MIN_CALLS_FOR_SHARE;
						const repName = rep.name || rep.userId;
						return (
							<React.Fragment key={rep.userId}>
								<tr>
									<td className={css(styleSheet.td, styleSheet.alignLeft)}>
										<div className={css(styleSheet.repCell)}>
											<Button
												variant='ghost'
												size='sm'
												styles={[styleSheet.repToggle]}
												aria-expanded={expanded}
												onClick={() => setExpandedRepId(expanded ? null : rep.userId)}
											>
												{repName}
											</Button>
											{isNeverFollower(rep) ? (
												<span className={css(styleSheet.neverFlag)}>never followed the coach</span>
											) : null}
										</div>
									</td>
									<td className={css(styleSheet.td)}>{rep.pastHookCalls}</td>
									<td className={css(styleSheet.td)}>{rep.followedCalls}</td>
									<td className={css(styleSheet.td)}>
										<span
											className={css(styleSheet.pillCell, thin ? styleSheet.faded : null)}
											title={thin ? `Fewer than ${MIN_CALLS_FOR_SHARE} calls past the hook` : undefined}
										>
											<Pill
												variant='tag'
												size='small'
												color={tierPillColor(shareTier(share))}
												label={formatPercent(share)}
											/>
										</span>
									</td>
									<td className={css(styleSheet.td)}>{rep.hookRejectedCalls}</td>
									<td className={css(styleSheet.td)}>{rep.hookRejectedFollowedCalls}</td>
									<td className={css(styleSheet.td)}>{rep.bookedCalls}</td>
									<td className={css(styleSheet.td)}>{rep.bookedFollowedCalls}</td>
									<td className={css(styleSheet.td)}>{rep.notScoredCalls}</td>
								</tr>
								{expanded ? (
									<tr>
										<td className={css(styleSheet.expandedCell)} colSpan={COLUMN_COUNT}>
											<ScoreboardRepCalls
												userId={rep.userId}
												repName={repName}
												start={range.start}
												end={range.end}
												followedScore={followedScore}
												onSelectCall={onSelectCall}
											/>
										</td>
									</tr>
								) : null}
							</React.Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
